import * as THREE from 'three';
import { BLACK, DSTEEL, DWOOD, WOOD, cloth, emis, leather, std, stone } from '../materials.ts';
import { G, M, V, glowSprite, limb, ring } from '../prims.ts';
import { mulberry32 } from '../rand.ts';
import { boxClad, type Era2Material } from './era2.ts';
import { BLOCK, GAP, GOLEM, IRON_ROOF, PLANK, PLANK_DK, PLINTH, RUNE, SACK, Y0, box, crate, fieldstones, quoinOf, quoins, rubbleColors, wallOf, yard, type LandOpts } from './land.ts';
import { IRON_BAND, IRON_PLATE, IRON_RIVET, crack, rubbleAround, snapTop, splinters, stain, type Wear } from './structures.ts';
import { begin, done, part, type BaseOpts, type Model } from './types.ts';

/*
 * The saberes' second and third stage pieces (docs/SABERES.md §4): the boulder on its chock at a slope's edge, the
 * kennel and its yard, the dwarven crane, the sieve over its pit, the kennel's mastiff, and what the keep shows for
 * the alarm and the props, the stone of names and the garrison's campfire.
 *
 * Performance (docs/RENDIMIENTO.md, docs/OBRAS.md §10): geometry only over the shared paints (materials.ts caches by
 * parameters: the same call is the same material), nothing see-through or two-sided, no lights; what glows is a
 * self-lit paint other models already use, and glow sprites the bake hands to the shared glow layer.
 */

const TAU = Math.PI * 2;
const ROPE = cloth('#6b6256');
const CHARRED = std('#2b2119', { roughness: 1 });
const BOULDER_STONE = stone('#7a7469');
const BOULDER_DK = stone('#6d6559');
/** Dragon-red rune light (upgrades.ts LEVEL_MATS.rune). */
const RED_RUNE = emis('#ffc0a8', '#ff4f3a', 1.3);
const EMBER = emis('#ff9a50', '#ff6a1a', 0.5);
const FORGE = emis('#ffb070', '#ff5a10', 1.4);
// Shared paints: the board's built pieces draw one multi-draw call per material (engine/structbatch.ts), so the
// saberes' structures reuse the paints other models already have rather than adding near-twins.
const STRAW = SACK;
const HAIR = leather('#3b2716');
const DUST = stone('#c4c8d0');
const DUST_FILM = stone('#9a9ea6');
const CRIMSON = cloth('#7a1d22');
/** Moss on the boulder (the grove's, models/levels.ts: the same cached paint). */
const MOSS_PATCH = std('#5d7a3a', { roughness: 1, bumpScale: 0.04 });

/** A small stone golem of the bastion, standing at its work (feet at the origin, facing +Z, about 0.55 tall). */
const golemAt = (parent: THREE.Object3D, x: number, z: number, ry: number, arms: 'reach' | 'low' = 'reach'): THREE.Group => {
  const gg = G(parent, x, Y0, z);
  gg.rotation.y = ry;
  for (const k of [-1, 1]) limb(gg, V(k * 0.06, 0.17, 0), V(k * 0.065, 0, 0.01), 0.045, 0.05, GOLEM, 5);
  M(new THREE.BoxGeometry(0.24, 0.24, 0.17), GOLEM, gg).position.y = 0.3;
  M(new THREE.BoxGeometry(0.13, 0.11, 0.13), GOLEM, gg).position.set(0, 0.48, 0.01);
  for (const k of [-1, 1]) box(gg, RUNE, 0.026, 0.018, 0.01, k * 0.032, 0.49, 0.077);
  box(gg, RUNE, 0.07, 0.016, 0.01, 0, 0.31, 0.088);
  const hand = arms === 'reach' ? [0.11, 0.36, 0.2] : [0.15, 0.2, 0.1];
  for (const k of [-1, 1]) limb(gg, V(k * 0.15, 0.38, 0), V(k * (hand[0] as number), hand[1] as number, hand[2] as number), 0.04, 0.045, GOLEM, 5);
  glowSprite(gg, '#4fdcff', 0.18, V(0, 0.49, 0.09), 0.4);
  return gg;
};

/** Cracks, stains and debris on a box body's faces: the usual wear (as obras.ts). */
const wearBox = (g: THREE.Object3D, mat: Era2Material, wear: Wear, faces: [number, number, number, number][], r: number, wr: () => number, cx = 0, cz = -0.1): void => {
  if (wear === 0) return;
  for (const [x, y, z, ry] of faces) {
    if (mat > 1 || wr() < 0.5) crack(g, x, y, z, ry, wear === 2 ? 0.22 : 0.15, wr);
    stain(g, x + 0.04, y - 0.1, z, ry, 0.06 + wr() * 0.03, wr, '#26221f');
  }
  rubbleAround(g, wear === 2 ? 8 : 3, r, wr, rubbleColors(mat), cx, cz);
  splinters(g, wear * 2, r - 0.05, wr, cx, cz);
};

/* ---------- the boulder ---------- */

export interface BoulderOpts extends BaseOpts {
  level?: number;
  material?: Era2Material;
  /** Spent: it rolled, the chock stands empty with its pin out and the rut it left runs downhill. */
  spent?: boolean;
}

/**
 * The boulder (docs/SABERES.md §4.1 «La galga»): a great round stone held on a timber chock at the edge of a slope,
 * a wedge pinned under it and a rope to the pin; facing +Z is downhill, the way it rolls. Its level shows: iron
 * straps on the chock from 2, a dragon-red rune cut in the stone from 3, the stone itself bigger at 4 and 5, iron
 * hoops round it at 5. Spent: the empty chock, the pin pulled and the rope slack, a rut and a few broken stones
 * running off down the slope.
 */
export const boulder = (o?: BoulderOpts): Model => {
  const lv = Math.max(1, Math.min(5, o?.level ?? 1));
  const mat = o?.material ?? 1;
  const c = begin(o, 0.6, 201);
  const { g } = c;
  const rng = mulberry32(2010 + lv);
  // The edge of the slope: a low bank of earth and stones falling away to +Z.
  const bank = M(new THREE.CylinderGeometry(0.5, 0.56, 0.08, 9), std('#4a4034', { roughness: 1, flatShading: true }), g);
  bank.position.set(0, 0.04, -0.06);
  bank.scale.z = 0.8;
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * TAU + rng();
    const s = M(new THREE.DodecahedronGeometry(0.04 + rng() * 0.03, 0), i % 2 ? PLINTH : BOULDER_DK, g);
    s.position.set(Math.sin(a) * 0.45, 0.05, Math.cos(a) * 0.38 - 0.06);
  }
  const iron = mat === 3 || lv >= 2;
  // The chock: two heavy cheeks with a sill between, a wedge at the front where the pin holds it.
  const ch = G(g, 0, 0.08, 0);
  for (const k of [-1, 1]) {
    const cheek = box(ch, DWOOD, 0.08, 0.14, 0.42, k * 0.2, 0.07, 0);
    cheek.rotation.x = 0.06;
    if (iron) for (const z of [-0.12, 0.12]) box(ch, IRON_BAND, 0.09, 0.15, 0.02, k * 0.2, 0.07, z);
  }
  box(ch, WOOD, 0.34, 0.05, 0.12, 0, 0.025, -0.14);
  const wedge = box(ch, WOOD, 0.3, 0.07, 0.08, 0, 0.05, 0.17);
  if (o?.spent) {
    // Knocked flat when it went.
    wedge.rotation.x = 1.2;
    wedge.position.set(0, 0.03, 0.26);
  } else wedge.rotation.x = -0.35;
  // The pin through the cheeks and the rope back to the trip stake behind.
  const pin = o?.spent ? V(0.32, 0.02, 0.3) : V(0, 0.12, 0.17);
  const pm = M(new THREE.CylinderGeometry(0.018, 0.018, 0.5, 6), iron ? DSTEEL : DWOOD, ch);
  pm.position.copy(pin);
  pm.rotation.z = Math.PI / 2;
  if (o?.spent) pm.rotation.y = 0.8;
  limb(g, V(-0.4, 0.05, -0.32), V(-0.4, 0.3, -0.32), 0.025, 0.02, DWOOD, 5);
  limb(g, V(-0.4, 0.27, -0.32), o?.spent ? V(-0.1, 0.02, 0.0) : V(-0.24, 0.2, 0.17), 0.006, 0.006, ROPE, 3);
  if (!o?.spent) {
    // The stone, a touch out of round, sitting in the chock.
    const r = 0.27 + (lv >= 4 ? 0.04 : 0) + (lv >= 5 ? 0.03 : 0);
    const st = G(g, 0, 0.1 + r, 0);
    const ball = M(new THREE.DodecahedronGeometry(r, 1), BOULDER_STONE, st);
    ball.scale.set(1, 0.94, 1.03);
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * TAU + 0.4;
      const chip = M(new THREE.DodecahedronGeometry(r * 0.28, 0), BOULDER_DK, st);
      chip.position.set(Math.sin(a) * r * 0.82, (rng() - 0.5) * r * 0.9, Math.cos(a) * r * 0.82);
      chip.scale.setScalar(0.7);
    }
    // A dark crack across its crown, and moss in the hollows: a stone that has lain out a long time.
    for (const [x, y, rz] of [[-0.06, 0.55, 0.5], [0.06, 0.42, -0.4], [0.14, 0.26, 0.7]] as const) {
      const cr = box(st, BLACK, 0.02, r * 0.5, 0.02, x * r * 3, y * r, r * 0.86);
      cr.rotation.z = rz;
      cr.rotation.x = -0.5;
    }
    for (let i = 0; i < 3; i++) {
      const a = 2.2 + i * 1.3;
      const mo = M(new THREE.DodecahedronGeometry(r * 0.22, 0), MOSS_PATCH, st);
      mo.position.set(Math.sin(a) * r * 0.85, -r * 0.35, Math.cos(a) * r * 0.85);
      mo.scale.y = 0.4;
    }
    {
      // The garrison's rune cut on its face toward the field, dragon red (brighter from level 3).
      if (lv < 3) for (const [w, h, y, rz] of [[0.012, 0.13, 0.02, 0], [0.09, 0.012, 0.05, 0.3], [0.09, 0.012, -0.02, -0.3]] as const) box(st, CRIMSON, w, h, 0.01, 0, y, r * 0.985).rotation.z = rz;
    }
    if (lv >= 3) {
      // A rune cut on its face toward the field, glowing dragon red.
      for (const [w, h, y, rz] of [[0.012, 0.13, 0.02, 0], [0.09, 0.012, 0.05, 0.3], [0.09, 0.012, -0.02, -0.3]] as const) {
        const rn = box(st, RED_RUNE, w, h, 0.01, 0, y, r * 0.98);
        rn.rotation.z = rz;
      }
      glowSprite(st, '#ff4f3a', 0.35, V(0, 0.02, r + 0.03), 0.35);
    }
    if (lv >= 5) for (const a of [0, Math.PI / 2]) {
      const hoop = M(new THREE.TorusGeometry(r * 1.01, 0.014, 4, 18), IRON_BAND, st);
      hoop.rotation.y = a;
    }
  } else {
    // The rut it left and what broke off it, down the slope.
    for (let i = 0; i < 4; i++) box(g, CHARRED, 0.16 - i * 0.02, 0.012, 0.2, (rng() - 0.5) * 0.04, 0.085 - i * 0.012, 0.34 + i * 0.2);
    for (let i = 0; i < 3; i++) M(new THREE.DodecahedronGeometry(0.05, 0), BOULDER_STONE, g).position.set((rng() - 0.5) * 0.3, 0.05, 0.4 + rng() * 0.3);
  }
  return done(c, o?.spent ? 0.35 : 0.75);
};

/* ---------- the kennel ---------- */

export interface KennelOpts extends LandOpts {
  level?: number;
}

/** A dog house: a pent box with an arched dark door toward +Z, straw in front. */
const dogHouse = (g: THREE.Object3D, x: number, z: number, ry: number, mat: Era2Material, wear: Wear, s = 1): void => {
  const dg = G(g, x, Y0, z);
  dg.rotation.y = ry;
  dg.scale.setScalar(s);
  const wallM = mat === 1 ? PLANK : wallOf(mat);
  box(dg, wallM, 0.26, 0.2, 0.24, 0, 0.1, 0);
  const roof = G(dg, 0, 0.22, 0);
  for (const k of [-1, 1]) {
    const slab = box(roof, mat === 3 ? IRON_ROOF : PLANK_DK, 0.15, 0.025, 0.3, k * 0.07, 0.03, 0);
    slab.rotation.z = -k * 0.55;
  }
  const arch = M(new THREE.CylinderGeometry(0.06, 0.06, 0.014, 8, 1, false, 0, Math.PI), BLACK, dg);
  arch.rotation.set(Math.PI / 2, 0, Math.PI / 2);
  arch.position.set(0, 0.1, 0.121);
  box(dg, BLACK, 0.12, 0.1, 0.012, 0, 0.05, 0.121);
  if (wear === 2) box(roof, GAP, 0.08, 0.012, 0.1, 0.06, 0.06, 0.02);
  const straw = M(new THREE.DodecahedronGeometry(0.05, 0), STRAW, dg);
  straw.position.set(0.04, 0.012, 0.17);
  straw.scale.set(1.4, 0.3, 1);
};

/**
 * The kennel (docs/SABERES.md §4.1 «La perrera»): a long timber kennel across the back of the yard, its dog doors
 * dark and open, a fenced run in front where the mastiffs wait, their iron bowls and a water trough, a chain post
 * and a gnawed bone, the kennel-master's horn on its peg. Its level shows a third dog house at 3 (KENNEL.dogs) and a
 * red pennant on the roof from 2. Plank and fieldstone in wood, a stone footing in stone, slate bound in iron in iron.
 * Worn (1): cracks and gaps. Crumbling (2): the fence broken through, a roof holed, a bowl knocked over.
 */
export const kennel = (o?: KennelOpts): Model => {
  const mat = o?.material ?? 1;
  const wear = o?.wear ?? 0;
  const lv = Math.max(1, Math.min(3, o?.level ?? 1));
  const c = begin(o, 0.85, 202);
  const { g } = c;
  const wr = mulberry32(2020 + mat * 3 + wear);
  const iron = mat === 3;
  yard(g, mat);
  // The kennel: a long low house across the back, a pent roof sloping to the front, three doors.
  const W = 0.96, D = 0.34, Z = -0.4, H = 0.36;
  const front = Z + D / 2;
  const wallM = mat === 1 ? PLANK : wallOf(mat);
  box(g, wallM, W, H, D, 0, Y0 + H / 2, Z);
  if (mat === 1) {
    for (let x = -W / 2 + 0.08; x < W / 2 - 0.04; x += 0.11) box(g, DWOOD, 0.016, H, 0.01, x, Y0 + H / 2, front + 0.004);
    fieldstones(g, 8, -W / 2 + 0.05, W / 2 - 0.05, Y0 + 0.01, Y0 + 0.07, front + 0.008, wr);
  } else quoins(g, quoinOf(mat), 0, Z, W, D, Y0, Y0 + H);
  const roof = G(g, 0, Y0 + H + 0.03, Z + 0.02);
  roof.rotation.x = 0.28;
  box(roof, iron ? IRON_ROOF : PLANK_DK, W + 0.1, 0.03, D + 0.14, 0, 0, 0);
  if (!iron) for (let x = -W / 2; x < W / 2; x += 0.1) box(roof, PLANK, 0.012, 0.012, D + 0.14, x + 0.03, 0.02, 0);
  else for (let x = -W / 2; x <= W / 2 + 0.01; x += 0.12) box(roof, IRON_BAND, 0.014, 0.02, D + 0.14, x, 0.022, 0);
  for (let i = 0; i < wear * 2; i++) box(roof, GAP, 0.1, 0.012, 0.08, (wr() - 0.5) * 0.7, 0.022, (wr() - 0.5) * 0.2);
  for (const x of [-0.3, 0, 0.3]) {
    const arch = M(new THREE.CylinderGeometry(0.07, 0.07, 0.014, 8, 1, false, 0, Math.PI), BLACK, g);
    arch.rotation.set(Math.PI / 2, 0, Math.PI / 2);
    arch.position.set(x, Y0 + 0.14, front + 0.006);
    box(g, BLACK, 0.14, 0.14, 0.012, x, Y0 + 0.07, front + 0.006);
    box(g, iron ? IRON_BAND : DWOOD, 0.17, 0.02, 0.02, x, Y0 + 0.22, front + 0.01);
  }
  if (iron) boxClad(g, 0, Z, W, D, Y0, Y0 + H, [Y0 + 0.26], Y0 + 0.08, ['l', 'r']);
  // The run: a rail fence round the front of the yard, a gate left open on the right.
  const posts: [number, number][] = [[-0.62, -0.2], [-0.56, 0.06], [-0.4, 0.3], [-0.2, 0.5], [0, 0.62], [0.2, 0.5], [0.4, 0.3], [0.56, 0.06], [0.62, -0.2]];
  posts.forEach(([x, z], i) => {
    if (wear === 2 && (i === 3 || i === 4)) {
      box(g, DWOOD, 0.03, 0.03, 0.22, x + 0.05, Y0 + 0.015, z + 0.06, 0.8);
      return;
    }
    box(g, iron ? IRON_BAND : DWOOD, 0.035, 0.24, 0.035, x, Y0 + 0.12, z);
    M(new THREE.ConeGeometry(0.022, 0.04, 4), DWOOD, g).position.set(x, Y0 + 0.26, z);
    const n = posts[i + 1];
    if (!n || i === 6 || (wear === 2 && (i === 2 || i === 3 || i === 4))) return;
    for (const y of [0.09, 0.19]) limb(g, V(x, Y0 + y, z), V(n[0], Y0 + y, n[1]), 0.012, 0.012, WOOD, 4);
  });
  // The gate, swung open.
  const gt = G(g, 0.4, Y0, 0.3);
  gt.rotation.y = -1.9;
  for (const y of [0.09, 0.19]) box(gt, WOOD, 0.26, 0.022, 0.022, 0.13, y, 0);
  box(gt, DWOOD, 0.022, 0.22, 0.022, 0.25, 0.11, 0);
  // Bowls, a trough, a chain post, a bone, straw.
  for (const [x, z] of [[-0.2, 0.05], [-0.06, 0.12], [0.08, 0.06]] as const) {
    const b = G(g, x, Y0, z);
    if (wear === 2 && x > 0) b.rotation.z = 1.4;
    M(new THREE.CylinderGeometry(0.05, 0.04, 0.03, 9), DSTEEL, b).position.y = 0.015;
    M(new THREE.CylinderGeometry(0.042, 0.042, 0.006, 9), DWOOD, b).position.y = 0.03;
  }
  box(g, WOOD, 0.22, 0.06, 0.08, 0.3, Y0 + 0.03, -0.06, 0.3);
  box(g, DSTEEL, 0.19, 0.01, 0.06, 0.3, Y0 + 0.062, -0.06, 0.3);
  limb(g, V(-0.38, Y0, 0.08), V(-0.38, Y0 + 0.26, 0.08), 0.022, 0.02, DWOOD, 5);
  ring(g, 0.03, 0.008, DSTEEL, Y0 + 0.22, 3, 8).position.set(-0.38, Y0 + 0.22, 0.08);
  for (let i = 0; i < 5; i++) M(new THREE.TorusGeometry(0.014, 0.004, 3, 6), DSTEEL, g).position.set(-0.35 + i * 0.025, Y0 + 0.012, 0.12 + i * 0.02);
  const bone = G(g, 0.0, Y0 + 0.012, 0.32);
  bone.rotation.y = 0.7;
  limb(bone, V(-0.05, 0, 0), V(0.05, 0, 0), 0.01, 0.01, std('#d5d8d1', { roughness: 0.6 }), 5);
  for (const k of [-1, 1]) M(new THREE.SphereGeometry(0.016, 5, 3), std('#d5d8d1', { roughness: 0.6 }), bone).position.set(k * 0.055, 0, 0);
  // The kennel-master's horn on a peg by the door.
  const horn = G(g, -0.44, Y0 + 0.28, front + 0.03);
  limb(horn, V(0, 0, 0), V(0.08, 0.03, 0.02), 0.012, 0.024, std('#d5d8d1', { roughness: 0.6 }), 6);
  limb(horn, V(-0.01, 0.0, 0), V(0.07, 0.05, 0.01), 0.003, 0.003, HAIR, 3);
  if (lv >= 2) {
    // A red pennant on a short mast at the roof's corner.
    const mg = G(g, W / 2 - 0.04, Y0 + H + 0.06, Z - 0.1);
    limb(mg, V(0, 0, 0), V(0, 0.36, 0), 0.01, 0.008, DWOOD, 4);
    const f = M(new THREE.ConeGeometry(0.04, 0.22, 3), CRIMSON, mg);
    f.rotation.z = -Math.PI / 2;
    f.scale.z = 0.2;
    f.position.set(0.11, 0.31, 0);
  }
  if (lv >= 3) dogHouse(g, -0.5, -0.02, 0.5, mat, wear, 1.1);
  wearBox(g, mat, wear, [[-0.15, Y0 + 0.26, front + 0.008, 0], [0.38, Y0 + 0.22, front + 0.008, 0], [W / 2 + 0.004, Y0 + 0.2, Z, Math.PI / 2]], 0.64, wr);
  return done(c, 0.75);
};

/* ---------- the crane ---------- */

export interface CraneOpts extends BaseOpts {
  material?: Era2Material;
  wear?: Wear;
}

/**
 * The crane of La Hondura (docs/SABERES.md §4.1 «La grúa»): a dwarven timber crane on a squat stone footing, a tall
 * braced mast with a long jib over the field, a chain from its tip lifting a dressed block, a box of stones hung at
 * the jib's back end for a counterweight; at the foot, the winch drum and the golem who stays at it, hands on the
 * handle. Timber on fieldstone in wood, a dressed footing in stone, slate bound in iron in iron. Worn (1): cracks, a
 * brace gone. Crumbling (2): the jib snapped and hanging, the block down, the chain slack.
 */
export const crane = (o?: CraneOpts): Model => {
  const mat = o?.material ?? 1;
  const wear = o?.wear ?? 0;
  const c = begin(o, 0.62, 203);
  const { g } = c;
  const wr = mulberry32(2030 + mat * 3 + wear);
  const iron = mat === 3;
  const footM = mat === 1 ? PLINTH : wallOf(mat);
  M(new THREE.CylinderGeometry(0.42, 0.46, 0.12, 8), footM, g).position.y = 0.06;
  M(new THREE.CylinderGeometry(0.3, 0.36, 0.1, 8), mat === 1 ? stone('#77736c') : quoinOf(mat), g).position.y = 0.17;
  if (mat === 1) for (let i = 0; i < 7; i++) {
    const a = (i / 7) * TAU;
    M(new THREE.DodecahedronGeometry(0.05, 0), PLINTH, g).position.set(Math.sin(a) * 0.43, 0.1, Math.cos(a) * 0.43);
  }
  const postM = iron ? IRON_BAND : DWOOD;
  // The mast: four legs leaning in, braced, a cap at the top that the jib turns on.
  const top = 1.35;
  for (const [x, z] of [[-0.16, -0.16], [0.16, -0.16], [0.16, 0.16], [-0.16, 0.16]] as const) limb(g, V(x, 0.22, z), V(x * 0.35, top, z * 0.35), 0.032, 0.026, WOOD, 5);
  for (const y of [0.55, 0.9]) {
    const k = 1 - ((y - 0.22) / (top - 0.22)) * 0.65;
    for (let i = 0; i < 4; i++) {
      if (wear >= 1 && i === 1 && y > 0.8) continue;
      const a = (i / 4) * TAU + Math.PI / 4;
      const b = ((i + 1) / 4) * TAU + Math.PI / 4;
      const r = 0.226 * k;
      limb(g, V(Math.sin(a) * r, y, Math.cos(a) * r), V(Math.sin(b) * r, y + 0.12, Math.cos(b) * r), 0.012, 0.012, postM, 4);
    }
  }
  if (iron) for (const y of [0.4, 0.8, 1.2]) ring(g, 0.2 * (1 - ((y - 0.22) / (top - 0.22)) * 0.65) + 0.03, 0.012, IRON_BAND, y, 4, 10);
  box(g, DWOOD, 0.18, 0.06, 0.18, 0, top, 0);
  // The jib toward the field (+Z), its stay to the mast's head, the counterweight at its back end.
  // Out along −X (across the table camera's view, so it reads at its length), its back end to +X.
  const jib = part(G(g, 0, top + 0.05, 0), 'arm');
  jib.rotation.y = -Math.PI / 2;
  const snapped = wear === 2;
  const reach = 0.85;
  limb(jib, V(0, 0, -0.35), V(0, 0, snapped ? 0.35 : reach), 0.035, 0.028, WOOD, 5);
  limb(jib, V(0, 0.3, 0), V(0, 0, snapped ? 0.3 : reach - 0.05), 0.008, 0.008, iron ? DSTEEL : ROPE, 3);
  limb(jib, V(0, 0, 0), V(0, 0.32, 0), 0.025, 0.02, WOOD, 4);
  if (snapped) {
    snapTop(jib, 0, 0, 0.35, 0.03, WOOD, wr);
    const hang = G(jib, 0, 0, 0.35);
    hang.rotation.x = 1.25;
    limb(hang, V(0, 0, 0), V(0, 0, 0.42), 0.03, 0.026, WOOD, 5);
  }
  const cw = G(jib, 0, -0.08, -0.32);
  box(cw, DWOOD, 0.2, 0.16, 0.16, 0, -0.06, 0);
  for (let i = 0; i < 4; i++) M(new THREE.DodecahedronGeometry(0.05, 0), BOULDER_DK, cw).position.set(-0.05 + (i % 2) * 0.1, 0.03, -0.04 + Math.floor(i / 2) * 0.08);
  for (const k of [-1, 1]) limb(cw, V(k * 0.09, 0.02, 0), V(0, 0.08, 0), 0.004, 0.004, DSTEEL, 3);
  // The chain from the jib's tip and the block it lifts (down on the footing when the jib is broken).
  const tipZ = snapped ? 0.75 : reach - 0.03;
  const chainTop = snapped ? 0.3 : top + 0.03;
  const blockY = snapped ? 0.06 : 0.62;
  let link = 0;
  for (let y = chainTop; y > blockY + 0.12; y -= 0.045) {
    const l = M(new THREE.TorusGeometry(0.014, 0.005, 3, 6), DSTEEL, g);
    l.position.set(-tipZ, y, 0);
    l.rotation.y = link++ % 2 ? Math.PI / 2 : 0;
  }
  const hook = G(g, -tipZ, blockY + 0.1, 0);
  M(new THREE.TorusGeometry(0.03, 0.008, 3, 8, Math.PI * 1.4), DSTEEL, hook);
  const blk = G(g, -tipZ, blockY, 0);
  if (snapped) blk.rotation.set(0.2, 0.6, 0.1);
  box(blk, BLOCK, 0.18, 0.12, 0.14, 0, 0, 0);
  for (const k of [-1, 1]) limb(blk, V(k * 0.09, 0.06, 0), V(0, 0.11, 0), 0.004, 0.004, ROPE, 3);
  // The winch drum and its golem at the foot, on the near side.
  const wz = 0.2, wx = 0.2;
  const drum = M(new THREE.CylinderGeometry(0.05, 0.05, 0.2, 9), WOOD, g);
  drum.rotation.z = Math.PI / 2;
  drum.position.set(wx, 0.3, wz);
  for (const k of [-1, 1]) {
    box(g, DWOOD, 0.03, 0.12, 0.08, wx + k * 0.11, 0.26, wz);
    const hoop = M(new THREE.TorusGeometry(0.055, 0.006, 3, 10), iron ? IRON_BAND : DSTEEL, g);
    hoop.position.set(wx + k * 0.09, 0.3, wz);
    hoop.rotation.y = Math.PI / 2;
  }
  limb(g, V(wx + 0.14, 0.3, wz), V(wx + 0.16, 0.38, wz + 0.08), 0.008, 0.008, DSTEEL, 3);
  limb(g, V(wx, 0.33, wz), V(0, chainTop - 0.3, 0), 0.004, 0.004, DSTEEL, 3);
  const gm = golemAt(g, wx + 0.22, wz + 0.4, Math.PI + 0.5);
  gm.position.y = 0;
  gm.scale.setScalar(0.85);
  if (wear > 0) {
    for (let i = 0; i < wear + 1; i++) crack(g, Math.sin(i * 2) * 0.43, 0.08, Math.cos(i * 2) * 0.43, i * 2, 0.12, wr);
    rubbleAround(g, wear === 2 ? 7 : 3, 0.45, wr, rubbleColors(mat));
    splinters(g, wear * 2, 0.45, wr);
  }
  return done(c, snapped ? 1.45 : 1.75);
};

/* ---------- the sieve ---------- */

/**
 * The sieve (docs/SABERES.md §4.1 «El tamiz»): a great timber frame on four legs, tilted over a dark pit at the
 * middle of the yard, its horsehair mesh stretched taut and a pale film of dream dust caught on it; sacks of sifted
 * dust and a shovel stacked against a lean-to at the back, and a golem at the frame's low end shaking it. Plank and
 * fieldstone in wood, dressed stone round the pit in stone, slate and iron in iron. Worn (1): cracks, the mesh torn
 * in a corner. Crumbling (2): a leg broken and the frame slumped into the pit, the lean-to holed.
 */
export const sieve = (o?: LandOpts): Model => {
  const mat = o?.material ?? 1;
  const wear = o?.wear ?? 0;
  const c = begin(o, 0.85, 204);
  const { g } = c;
  const wr = mulberry32(2040 + mat * 3 + wear);
  const iron = mat === 3;
  yard(g, mat);
  // The pit: a dark hollow ringed in stone.
  const pz = 0.06;
  const pit = M(new THREE.CylinderGeometry(0.34, 0.3, 0.02, 12), BLACK, g);
  pit.position.set(0, Y0 + 0.002, pz);
  pit.scale.z = 0.72;
  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * TAU;
    const s = box(g, mat === 1 ? PLINTH : quoinOf(mat), 0.13, 0.05, 0.08, Math.sin(a) * 0.36, Y0 + 0.025, pz + Math.cos(a) * 0.27, a);
    s.rotation.y = a;
  }
  // The frame on its legs, tilted down toward the golem (+X).
  const fr = G(g, 0, Y0 + 0.26, pz);
  const slump = wear === 2;
  fr.rotation.z = slump ? -0.5 : -0.16;
  if (slump) fr.position.y -= 0.1;
  const FW = 0.84, FD = 0.5;
  const frameM = iron ? IRON_BAND : DWOOD;
  for (const k of [-1, 1]) {
    box(fr, frameM, FW, 0.04, 0.04, 0, 0, k * FD / 2);
    box(fr, frameM, 0.04, 0.04, FD, k * FW / 2, 0, 0);
  }
  // The horsehair mesh, and the pale film of dust caught on it.
  for (let i = 1; i < 9; i++) box(fr, HAIR, 0.006, 0.006, FD, -FW / 2 + (i * FW) / 9, 0.004, 0);
  for (let i = 1; i < 6; i++) box(fr, HAIR, FW, 0.006, 0.006, 0, 0.006, -FD / 2 + (i * FD) / 6);
  // Patches of it, thickest where it was thrown on, thinning toward the low end.
  for (let i = 0; i < 7; i++) {
    const d = M(new THREE.DodecahedronGeometry(0.07 + wr() * 0.04, 0), DUST_FILM, fr);
    d.position.set(-0.3 + i * 0.07 + (wr() - 0.5) * 0.05, 0.012, (wr() - 0.5) * 0.3);
    d.scale.set(1.2, 0.12, 1);
  }
  if (wear >= 1) box(fr, GAP, 0.12, 0.014, 0.1, FW / 2 - 0.1, 0.012, FD / 2 - 0.08);
  for (const [x, z] of [[-1, -1], [-1, 1], [1, -1], [1, 1]] as const) {
    if (slump && x > 0 && z > 0) continue;
    limb(g, V(x * (FW / 2 - 0.02), Y0 + 0.26 - x * 0.065, pz + z * (FD / 2 - 0.02)), V(x * (FW / 2 + 0.03), Y0, pz + z * (FD / 2 + 0.03)), 0.018, 0.02, WOOD, 4);
  }
  // A pale haze of dust over the mesh.
  glowSprite(g, '#e4ecff', 0.5, V(-0.1, Y0 + 0.36, pz), 0.18);
  // The lean-to at the back with sacks of sifted dust and a shovel.
  const lz = -0.46;
  box(g, mat === 1 ? PLANK : wallOf(mat), 0.6, 0.32, 0.04, -0.15, Y0 + 0.16, lz - 0.08);
  const lr = G(g, -0.15, Y0 + 0.34, lz + 0.02);
  lr.rotation.x = 0.4;
  box(lr, iron ? IRON_ROOF : PLANK_DK, 0.68, 0.025, 0.26, 0, 0, 0);
  if (wear === 2) box(lr, GAP, 0.15, 0.012, 0.1, 0.1, 0.018, 0);
  for (const k of [-1, 1]) box(g, frameM, 0.035, 0.3, 0.035, -0.15 + k * 0.28, Y0 + 0.15, lz + 0.1);
  for (const [x, s] of [[-0.35, 0.07], [-0.22, 0.065], [-0.28, 0.06]] as const) {
    const sk = M(new THREE.DodecahedronGeometry(s, 0), SACK, g);
    sk.position.set(x, Y0 + s * (x === -0.28 ? 2.2 : 0.75), lz + 0.04);
    sk.scale.set(1, 0.85, 0.85);
    // Tied at the neck with a pale tag of the Vigil's dust.
    box(sk, DUST, 0.03, 0.02, 0.01, 0, s * 0.6, s * 0.7);
  }
  crate(g, 0.12, 0.02, Y0, lz + 0.02, 0.2);
  const sh = G(g, 0.14, Y0, lz + 0.05);
  sh.rotation.z = 0.25;
  limb(sh, V(0, 0.05, 0), V(0, 0.38, 0), 0.01, 0.01, WOOD, 4);
  box(sh, DSTEEL, 0.07, 0.09, 0.01, 0, 0.04, 0);
  // The golem at the frame's low end, shaking it.
  golemAt(g, FW / 2 + 0.12, pz + 0.02, -Math.PI / 2, 'reach');
  if (iron) boxClad(g, -0.15, lz - 0.08, 0.6, 0.04, Y0, Y0 + 0.32, [Y0 + 0.2], Y0 + 0.1, ['f']);
  wearBox(g, mat, wear, [[-0.3, Y0 + 0.2, lz - 0.055, 0], [0.05, Y0 + 0.18, lz - 0.055, 0]], 0.62, wr);
  return done(c, 0.7);
};

/** The sieve's catch waiting to be sifted (CampaignState.sift): pale heaps of dream dust by the pit, faintly lit. */
export const siftDust = (o?: BaseOpts): Model => {
  const c = begin(o, 0.6, 205);
  const { g } = c;
  for (const [x, z, s] of [[-0.3, 0.32, 0.12], [-0.12, 0.42, 0.09], [0.3, 0.36, 0.1]] as const) {
    const h = M(new THREE.ConeGeometry(s, s * 0.9, 8), DUST, g);
    h.position.set(x, Y0 + s * 0.45, z);
    glowSprite(g, '#e4ecff', s * 4, V(x, Y0 + s, z), 0.3);
  }
  return done(c, 0.2);
};

/* ---------- ruins ---------- */

/** What a destroyed boulder chock, kennel, crane or sieve leaves, in its material. */
export const saberRuin = (type: string, mat: Era2Material = 2): Model => {
  const c = begin(undefined, 0.8, 206);
  const { g } = c;
  const rng = mulberry32(2060 + type.length * 13 + type.charCodeAt(0) + mat * 5);
  const wallM = wallOf(mat);
  const colors = rubbleColors(mat);
  const scorch = (r: number, x: number, z: number, sx: number, y = Y0): void => {
    const d = M(new THREE.CircleGeometry(r, 12), std('#1e1a16', { roughness: 1, bumpScale: 0 }), g);
    d.rotation.x = -Math.PI / 2;
    d.scale.x = sx;
    d.position.set(x, y + 0.004, z);
  };
  const ironBits = (n: number): void => {
    if (mat !== 3) return;
    for (let i = 0; i < n; i++) {
      const a = rng() * TAU;
      const d = 0.25 + rng() * 0.25;
      box(g, IRON_PLATE, 0.12, 0.012, 0.08, Math.sin(a) * d, Y0 + 0.02, Math.cos(a) * d - 0.1, rng() * 3).rotation.x = (rng() - 0.5) * 0.6;
    }
  };
  let h: number;
  if (type === 'crane') {
    M(new THREE.CylinderGeometry(0.42, 0.46, 0.12, 8), mat === 1 ? PLINTH : wallM, g).position.y = 0.06;
    scorch(0.4, 0, 0, 1, 0.12);
    // The mast down across the ground, the jib beside it, the counterweight's stones spilled.
    for (const [x, z, ry, len] of [[0.1, 0.1, 0.6, 1.1], [-0.2, 0.25, -0.4, 0.8]] as const) {
      const b = G(g, x, 0.16, z);
      b.rotation.y = ry;
      limb(b, V(-len / 2, 0, 0), V(len / 2, 0, 0), 0.03, 0.026, rng() < 0.5 ? CHARRED : WOOD, 5);
    }
    for (let i = 0; i < 6; i++) M(new THREE.DodecahedronGeometry(0.05, 0), BOULDER_DK, g).position.set(-0.3 + rng() * 0.2, 0.14, -0.3 + rng() * 0.2);
    box(g, BLOCK, 0.18, 0.12, 0.14, 0.35, 0.18, -0.2, 0.7);
    rubbleAround(g, 9, 0.45, rng, colors);
    h = 0.3;
  } else {
    yard(g, mat);
    scorch(0.48, 0, -0.2, 1.4);
    for (let i = 0; i < 6; i++) {
      const sh = 0.05 + rng() * 0.15;
      box(g, mat === 1 ? CHARRED : wallM, 0.15, sh, 0.05, -0.42 + i * 0.17, Y0 + sh / 2, -0.5);
    }
    for (let i = 0; i < 5; i++) box(g, i % 2 ? CHARRED : PLANK, 0.06, 0.02, 0.36 + rng() * 0.2, -0.36 + i * 0.18, Y0 + 0.02 + (i % 2) * 0.02, -0.2 + (rng() - 0.5) * 0.2, 0.3 + rng() * 0.6);
    if (type === 'kennel') {
      for (let i = 0; i < 5; i++) {
        const p = box(g, DWOOD, 0.03, 0.03, 0.24, -0.45 + i * 0.22, Y0 + 0.015, 0.3 + rng() * 0.15, rng() * 3);
        p.rotation.z = 0.1;
      }
      for (const x of [-0.2, 0.1]) M(new THREE.CylinderGeometry(0.05, 0.04, 0.03, 9), DSTEEL, g).position.set(x, Y0 + 0.015, 0.1);
    } else {
      // The sieve's frame broken in the pit, its mesh in tatters.
      M(new THREE.CylinderGeometry(0.34, 0.3, 0.02, 12), BLACK, g).position.set(0, Y0 + 0.002, 0.06);
      const fr = G(g, 0, Y0 + 0.08, 0.06);
      fr.rotation.set(0.1, 0.4, -0.35);
      for (const k of [-1, 1]) box(fr, CHARRED, 0.8, 0.04, 0.04, 0, 0, k * 0.24);
      for (let i = 0; i < 4; i++) box(fr, HAIR, 0.006, 0.006, 0.45, -0.3 + i * 0.2, 0.01, 0);
      for (let i = 0; i < 3; i++) {
        const sk = M(new THREE.DodecahedronGeometry(0.06, 0), SACK, g);
        sk.position.set(-0.35 + i * 0.12, Y0 + 0.04, -0.3);
        sk.scale.set(1, 0.6, 0.85);
      }
    }
    rubbleAround(g, mat === 1 ? 4 : 9, 0.55, rng, colors, 0, -0.15);
    splinters(g, 6, 0.5, rng, 0, -0.1);
    h = 0.28;
  }
  ironBits(4);
  return done(c, h);
};

/** The structures this module draws. */
export const SABER_TYPES = ['boulder', 'kennel', 'crane', 'sieve'];

/* ---------- the kennel's mastiff ---------- */

// A light grey-brown, so it stands out on the field's dark ground at the battle camera.
const DOG = std('#8a7f72', { roughness: 0.95, flatShading: true });
const DOG_DK = std('#4a423a', { roughness: 0.95, flatShading: true });
const DOG_LT = std('#b3aa9f', { roughness: 0.95, flatShading: true });
/** The collar in the garrison's red, broad enough to read from far. */
const COLLAR = cloth('#a3221c');

/**
 * The kennel's mastiff (docs/SABERES.md §4.1): a big grey-brown war dog, deep in the chest and heavy in the head, ears
 * cropped, a dark muzzle and a pale blaze, a broad studded leather collar with the garrison's red on it. Faces +Z.
 * Its tick steps it through a four-frame trot (the library bakes one frame each), the jaw dropping on the lunge.
 */
export const mastiff = (o?: BaseOpts): Model => {
  const c = begin(o, 0.42, 207);
  const { g } = c;
  const body = G(g, 0, 0.48, 0);
  const torso = M(new THREE.IcosahedronGeometry(0.28, 1), DOG, body);
  torso.scale.set(0.82, 0.8, 1.45);
  const chest = M(new THREE.IcosahedronGeometry(0.2, 1), DOG_LT, body);
  chest.position.set(0, -0.06, 0.24);
  chest.scale.set(0.95, 1.05, 0.8);
  const back = M(new THREE.IcosahedronGeometry(0.2, 0), DOG_DK, body);
  back.position.set(0, 0.12, -0.05);
  back.scale.set(0.75, 0.35, 1.5);
  // The head: broad skull, a short heavy muzzle, cropped ears.
  const head = G(body, 0, 0.14, 0.4);
  M(new THREE.IcosahedronGeometry(0.15, 1), DOG, head).scale.set(1.05, 0.9, 1);
  const muzzle = M(new THREE.BoxGeometry(0.14, 0.1, 0.14), DOG_DK, head);
  muzzle.position.set(0, -0.04, 0.14);
  M(new THREE.SphereGeometry(0.03, 6, 4), BLACK, head).position.set(0, -0.01, 0.215);
  const jaw = G(head, 0, -0.08, 0.08);
  box(jaw, DOG_DK, 0.11, 0.035, 0.13, 0, 0, 0.05);
  box(head, DOG_LT, 0.04, 0.1, 0.02, 0, 0.06, 0.13);
  for (const k of [-1, 1]) {
    const ear = M(new THREE.ConeGeometry(0.04, 0.08, 4), DOG_DK, head);
    ear.position.set(k * 0.09, 0.12, -0.03);
    ear.rotation.z = -k * 0.3;
    M(new THREE.SphereGeometry(0.018, 5, 3), BLACK, head).position.set(k * 0.065, 0.04, 0.12);
    // Heavy jowls.
    const jowl = M(new THREE.SphereGeometry(0.045, 6, 4), DOG_DK, head);
    jowl.position.set(k * 0.06, -0.07, 0.13);
  }
  // The collar: broad leather, iron studs, a red tag.
  const col = M(new THREE.TorusGeometry(0.15, 0.035, 4, 12), COLLAR, body);
  col.position.set(0, 0.08, 0.36);
  col.rotation.x = 1.2;
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * TAU;
    M(new THREE.SphereGeometry(0.012, 4, 3), IRON_RIVET, col).position.set(Math.sin(a) * 0.13, Math.cos(a) * 0.13, 0.02);
  }
  box(body, CRIMSON, 0.05, 0.06, 0.012, 0, -0.07, 0.43);
  const tail = G(body, 0, 0.06, -0.38);
  limb(tail, V(0, 0, 0), V(0, 0.08, -0.2), 0.035, 0.02, DOG, 5);
  const legs: THREE.Group[] = [];
  for (const [x, z] of [[-0.12, 0.24], [0.12, 0.24], [-0.12, -0.24], [0.12, -0.24]] as [number, number][]) {
    const leg = G(body, x, -0.12, z);
    limb(leg, V(0, 0, 0), V(0, -0.36, 0.02), 0.06, 0.04, DOG, 5);
    M(new THREE.SphereGeometry(0.045, 6, 4), DOG_DK, leg).position.set(0, -0.36, 0.04);
    legs.push(leg);
  }
  const t = (n: number): void => {
    const a = [0.5, 0, -0.5, 0][n & 3] as number;
    legs.forEach((l, i) => (l.rotation.x = (i === 0 || i === 3 ? 1 : -1) * a));
    tail.rotation.y = Math.sin(n * 1.5) * 0.3;
    body.position.y = 0.48 + ([0.04, 0, 0.04, 0][n & 3] as number);
    jaw.rotation.x = (n & 3) === 2 ? 0.4 : 0;
  };
  t(0);
  return done(c, 0.85, t);
};

/* ---------- the keep's rites, the stone of names, the campfire ---------- */

export interface AlarmBell extends Model {
  /** Swings the cracked bell for a moment (its tick then lets it settle). */
  ring(): void;
}

/**
 * The alarm (docs/SABERES.md §4.3 «Rebato»): a timber bell frame on a stone step with the garrison's small cracked
 * bell hung from it, a dark crack down its side, the rope tied off to a post; every now and then it swings. At the
 * keep's scale (engine: in a free cell of the keep's yard, like the stone of names).
 */
export const alarmFrame = (o?: BaseOpts): AlarmBell => {
  const c = begin(o, 0.6, 208);
  const { g } = c;
  const at = G(g, 0, 0, 0);
  box(at, PLINTH, 0.6, 0.08, 0.32, 0, 0.04, 0);
  for (const k of [-1, 1]) {
    limb(at, V(k * 0.24, 0.08, 0), V(k * 0.2, 0.95, 0), 0.035, 0.03, DWOOD, 5);
    for (const s of [-1, 1]) limb(at, V(k * 0.24, 0.08, s * 0.12), V(k * 0.22, 0.45, 0), 0.015, 0.015, DWOOD, 4);
  }
  box(at, DWOOD, 0.56, 0.06, 0.08, 0, 0.97, 0);
  // A little roof of shingles over the beam.
  for (const k of [-1, 1]) {
    const r = box(at, PLANK_DK, 0.6, 0.02, 0.14, 0, 1.04, k * 0.06);
    r.rotation.x = k * 0.5;
  }
  const bell = part(G(at, 0, 0.94, 0), 'bell');
  M(new THREE.CylinderGeometry(0.012, 0.012, 0.06, 5), DSTEEL, bell).position.y = -0.03;
  const body = M(new THREE.LatheGeometry([V(0, -0.06, 0), V(0.05, -0.065, 0), V(0.08, -0.12, 0), V(0.1, -0.22, 0), V(0.13, -0.29, 0), V(0.125, -0.3, 0), V(0, -0.27, 0)].map((p) => new THREE.Vector2(p.x, p.y)), 10), std('#4f6662', { roughness: 0.5, metalness: 0.35, flatShading: true, emissive: '#16211f', emissiveIntensity: 0.6 }), bell);
  body.rotation.y = 0.3;
  // The crack down its side, and a red rag tied to the clapper.
  for (const [y, rz] of [[-0.12, 0.3], [-0.18, -0.25], [-0.24, 0.2]] as const) {
    const cr = box(bell, BLACK, 0.008, 0.07, 0.008, 0.1 - (y + 0.12) * 0.25, y, 0.05);
    cr.rotation.z = rz;
  }
  M(new THREE.SphereGeometry(0.025, 5, 4), DSTEEL, bell).position.y = -0.29;
  box(bell, CRIMSON, 0.03, 0.08, 0.008, 0, -0.34, 0.01);
  limb(at, V(0.24, 0.6, 0.04), V(0.32, 0.08, 0.16), 0.006, 0.006, ROPE, 3);
  let swing = 99;
  const tick = (n: number): void => {
    // Every eight seconds or so it swings a few times and settles; `ring` starts one now.
    const t = n % 96;
    const k = swing < 36 ? swing : t < 36 ? t : -1;
    if (swing < 36) swing++;
    bell.rotation.z = k < 0 ? 0 : Math.sin(k * 0.55) * 0.55 * (1 - k / 36);
  };
  const m = done(c, 1.1, tick) as AlarmBell;
  m.ring = () => {
    swing = 0;
  };
  return m;
};

/**
 * The props (docs/SABERES.md §4.3 «Los puntales»): two oak beams crossed and braced on the inside of a gate, their
 * feet in the ground on chocks, a cross-tie lashed where they meet. Faces +Z (inward, away from the gate).
 */
export const gateProps = (o?: BaseOpts): Model => {
  const c = begin(o, 0.5, 209);
  const { g } = c;
  const OAK = std('#6a4a30', { roughness: 0.9, flatShading: true });
  for (const k of [-1, 1]) {
    // From the gate's top corner down to the ground well inside.
    limb(g, V(k * 0.36, 0.86, -0.04), V(-k * 0.26, 0.02, 0.62), 0.06, 0.065, OAK, 6);
    box(g, DWOOD, 0.14, 0.08, 0.12, -k * 0.27, 0.04, 0.66);
    box(g, IRON_BAND, 0.12, 0.03, 0.14, k * 0.34, 0.84, -0.02);
  }
  // A shorter brace straight back to the ground, and the cross-tie lashed where the beams cross.
  limb(g, V(0, 0.55, 0.0), V(0, 0.02, 0.78), 0.05, 0.055, OAK, 6);
  box(g, DWOOD, 0.12, 0.08, 0.12, 0, 0.04, 0.8);
  box(g, OAK, 0.7, 0.07, 0.07, 0, 0.42, 0.24);
  for (const k of [-0.03, 0.03]) ring(g, 0.06, 0.008, ROPE, 0.42, 3, 8).position.set(k, 0.42, 0.24);
  return done(c, 0.95);
};

/** The carved lines' ember (the glyph stone's lit marks, trophies.ts: the same cached paint). */
const NAME_LIT = emis('#ff8a6e', '#ff4f3a', 2.6);

/**
 * The stone of names (docs/SABERES.md §4.5): a tall grey stone by the keep on a low dark footing, leaning back a little
 * so its face looks up at the table camera; the Vigil's eye cut at its head. A line is carved for each Marea won since it
 * was found, two columns of cut names deep in the stone, each groove dark with dragon-ember light in it. With none
 * (`lines` 0) it stands uncarved: only the eye, and the first line scored faintly where the names will start. Built at
 * the keep's scale (engine: a free cell of the keep's yard, like the lectern).
 */
export const namestone = (o?: BaseOpts & { lines?: number }): Model => {
  const n = Math.max(0, Math.min(80, Math.floor(o?.lines ?? 0)));
  const c = begin(o, 0.5, 210);
  const { g } = c;
  const rng = mulberry32(2100);
  M(new THREE.CylinderGeometry(0.46, 0.52, 0.12, 7), stone('#4a4c53'), g).position.y = 0.06;
  const st = G(g, 0, 0.12, 0);
  st.rotation.x = -0.22;
  const S = stone('#9a9ea6');
  const H = 1.75;
  M(new THREE.BoxGeometry(0.62, H, 0.22), S, st).position.y = H / 2;
  const cap = M(new THREE.CylinderGeometry(0.31, 0.31, 0.22, 8, 1, false, 0, Math.PI), S, st);
  cap.rotation.set(Math.PI / 2, 0, Math.PI / 2);
  cap.position.y = H;
  // The Vigil's eye at the head, cut and lit.
  const eye = M(new THREE.TorusGeometry(0.08, 0.016, 3, 12), NAME_LIT, st);
  eye.position.set(0, H - 0.04, 0.112);
  eye.scale.y = 0.55;
  M(new THREE.SphereGeometry(0.026, 6, 4), NAME_LIT, st).position.set(0, H - 0.04, 0.114);
  glowSprite(st, '#ff6a4a', 0.5, V(0, H - 0.04, 0.14), 0.35);
  const GROOVE = stone('#3b3f47');
  // The names: a groove a line, two columns, from the top down; the ember fill sits in it, a little proud of its floor.
  const rowH = 0.04;
  const top = H - 0.2;
  const rows = Math.max(1, Math.ceil(n / 2));
  if (n === 0) {
    // Uncarved: the first line only scored, waiting.
    box(st, GROOVE, 0.44, 0.012, 0.01, 0, top, 0.111);
  }
  for (let i = 0; i < n; i++) {
    const col = i % 2;
    const y = top - Math.floor(i / 2) * rowH;
    const x0 = col ? 0.02 : -0.27;
    let x = x0;
    const words = 2 + Math.floor(rng() * 2);
    for (let w = 0; w < words && x < x0 + 0.23; w++) {
      const len = Math.min(0.05 + rng() * 0.05, x0 + 0.25 - x);
      box(st, GROOVE, len + 0.01, 0.026, 0.02, x + len / 2, y, 0.105);
      box(st, NAME_LIT, len, 0.014, 0.012, x + len / 2, y, 0.114);
      x += len + 0.02;
    }
  }
  // Their glow: one soft bloom over the carved part, as tall as it is.
  if (n > 0) {
    const h = rows * rowH;
    for (let k = 0; k < Math.min(3, Math.ceil(rows / 8)); k++) glowSprite(st, '#ff6a4a', 0.55 + h * 0.4, V(0, top - (h * (k + 0.5)) / Math.min(3, Math.ceil(rows / 8)), 0.16), 0.22);
  }
  return done(c, H + 0.35);
};

/**
 * The garrison's campfire (docs/SABERES.md §4.5 «La canción»): a ring of stones, crossed logs burning low, a pot on a
 * tripod and two log benches; its glow is the bake's and the shared glow layer's, no light.
 */
export const campfire = (o?: BaseOpts): Model => {
  const c = begin(o, 0.5, 211);
  const { g } = c;
  for (let i = 0; i < 9; i++) {
    const a = (i / 9) * TAU;
    M(new THREE.DodecahedronGeometry(0.05, 0), i % 2 ? PLINTH : BOULDER_DK, g).position.set(Math.sin(a) * 0.2, 0.035, Math.cos(a) * 0.2);
  }
  M(new THREE.CylinderGeometry(0.17, 0.17, 0.012, 10), EMBER, g).position.y = 0.012;
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI;
    const lg = G(g, 0, 0.07, 0);
    lg.rotation.set(0.35, a, 0);
    limb(lg, V(0, -0.05, -0.16), V(0, 0.1, 0.0), 0.025, 0.02, CHARRED, 5);
  }
  for (let i = 0; i < 5; i++) M(new THREE.DodecahedronGeometry(0.035, 0), FORGE, g).position.set(Math.sin(i * 1.3) * 0.06, 0.04 + (i % 2) * 0.03, Math.cos(i * 1.3) * 0.06);
  M(new THREE.ConeGeometry(0.06, 0.18, 6), FORGE, g).position.y = 0.16;
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * TAU;
    limb(g, V(Math.sin(a) * 0.24, 0, Math.cos(a) * 0.24), V(0, 0.46, 0), 0.008, 0.008, DSTEEL, 3);
  }
  M(new THREE.CylinderGeometry(0.06, 0.05, 0.07, 8), BLACK, g).position.y = 0.33;
  for (const [x, z, ry] of [[0.45, 0.1, 0.3], [-0.4, 0.22, -0.4]] as const) {
    const b = G(g, x, 0.05, z);
    b.rotation.y = ry;
    M(new THREE.CylinderGeometry(0.05, 0.05, 0.42, 7), WOOD, b).rotation.x = Math.PI / 2;
  }
  glowSprite(g, '#ff8a3a', 1.2, V(0, 0.2, 0), 0.6);
  glowSprite(g, '#ff6a1a', 3.0, V(0, 0.05, 0), 0.14);
  return done(c, 0.5);
};
